import {DropDown} from "./DropDown.js";
import {CalendarWidget} from "./CalendarWidget.js";
import {Temp} from "../utils/Temp.js";
import {SymbolicCalendarWidget} from "./SymbolicCalendarWidget.js";
import {msg} from "@/i18n/Message.ts";

export class DatePicker extends DropDown {
	/**
	 * Create calendar picker
	 * @param {jQuery} widget
	 * @param {jQuery} container
	 * @param {number} month
	 * @param {number} year
	 * @param {boolean} symbolic
	 */
	constructor(widget, container, month, year, symbolic) {
		if (container === null) container = widget.parent();
		super(widget);
		const calendarDefinition = Server.calendar;
		this.calendar = new CalendarWidget(month, year, calendarDefinition.firstDayOfWeek, calendarDefinition.days);
		if (symbolic) this.symbolic = new SymbolicCalendarWidget();

		this.rebuild();	// pre-build
	}

	makePanel() {
		const panel = super.makePanel();
		panel.addClass('dt-picker');

		const pickArea = Temp.tag('div', {clazz: 'dt-content'});
		panel.append(pickArea);

		this.classicCalendar = this.calendar.ui;
		pickArea.append(this.classicCalendar);

		if (this.symbolic) {
			const pickControls = Temp.tag('div', {clazz: 'dt-controls'});
			panel.append(pickControls);
			const switchLabel = $("<label></label>");
			pickControls.append(switchLabel);
			this.symSwitch = $('<input type="checkbox">');
			this.symSwitch.on("change", this.modeSwitch.bind(this));
			switchLabel.append(this.symSwitch);
			switchLabel.append(document.createTextNode(" "+msg("date.picker.dynamic")));

			this.symbolicCalendar = this.symbolic.ui;
			pickArea.append(this.symbolicCalendar);
		}

		this.activate();

		return panel;
	}
	/*
		Notes:
		User enters manual date: CHB=false, Show classic panel, move panel to the date (month, year)
		User enters symbolic date: CHB=true, Show symbolic panel, call parse() to init panel
		User enters error: CHB=false, Show classic panel, move panel to TODAY
		User enables CHB: Show symbolic panel, call parse("NOW()"), call listeners (this updates input field)
		User disables CHB: Show classic panel, init panel to TODAY, call listeners (---||---)
	 */

	get symbolicMode() {
		return this.symbolic && this.symSwitch.is(":checked");
	}

	getMinHeight() {
		return 180;
	}

	/**
	 * @param {boolean} symMode
	 */
	set symbolicMode(symMode) {
		let currentMode = this.symbolicMode;
		if (currentMode === symMode) return;
		if (symMode) {
			if (this.symbolic) this.symSwitch.prop("checked", true);
			else throw "Cannot enable symbolic date entry on a field which doesn't support it";
		} else {
			if (this.symbolic) this.symSwitch.prop("checked", false);
		}
		this.activate();
	}

	modeSwitch() {
		this.activate();
		if (this.symbolicMode) {
			this.symbolic.parse("NOW()");
			this.symbolic.fire();
		} else {
			const today = new Date();
			this.calendar.changeMonthYear(today.getMonth(), today.getFullYear(), true);
		}
	}

	activate() {
		if (this.symbolicMode) {
			this.symbolicCalendar.show();
			this.classicCalendar.hide();
		} else {
			if (this.symbolicCalendar) this.symbolicCalendar.hide();
			this.classicCalendar.show();
		}
	}

	addDatePickListener(listener) {
		this.calendar.addDatePickListener(listener);
	}

	addSymbolicPickListener(listener) {
		if (this.symbolic) this.symbolic.addSymbolicPickListener(listener);
	}

	/**
	 * User just entered valid date into input field
	 * @param {number} year
	 * @param {number} month
	 * @param {number} day
	 */
	dateChanged(year, month, day) {
		if (typeof year === 'undefined') {
			const today = new Date();
			year = today.getFullYear();
			month = today.getMonth();
			day = today.getDate();
		}
		this.symbolicMode = false;
		this.calendar.changeMonthYear(month, year, false);
	}

	/**
	 * User just entered valid symbolic representation of a date into input field
	 * @param {string} symbol - valid symbolic date, e.g. NOW() or MSTART(-5)
	 */
	symbolicDateChanged(symbol) {
		if (this.symbolic) {
			this.symbolicMode = true;
			this.symbolic.parse(symbol);
		}
	}

	shouldBeVisible(optionalData) {
		super.shouldBeVisible(optionalData);
		if (optionalData) {
			if (optionalData.symbolic && this.symbolic) {
				this.symSwitch.attr("checked", "checked");
				this.symbolic.parse(optionalData.value);
				this.activate();
			}
		}
	}
}
